import { useCallback } from 'react';
import { PageStatus } from '@/types/store';
import { stanNotify } from '@/lib/stanNotify';
import { useReorderFunnelPages, useUpdateFunnelPage } from '../queries/funnel-page-mutations';
import type { FunnelPageItem } from '../types';

interface UseFunnelReorderPagesArgs {
    storeId: number | undefined;
    savedFunnelPages: FunnelPageItem[];
}

/**
 * Reorder saved funnel pages. If the new entry page (index 0) has routing
 * branches attached (entry pages cannot have branches), its routing is cleared
 * first, then the reorder is committed.
 */
export function useFunnelReorderPages({ storeId, savedFunnelPages }: UseFunnelReorderPagesArgs) {
    const reorderFunnelPages = useReorderFunnelPages(storeId ?? 0);
    const updateFunnelPage = useUpdateFunnelPage(storeId ?? 0, true);

    return useCallback(
        (orderedSavedPageIds: number[]) => {
            if (!storeId) return;
            const payload = orderedSavedPageIds.map((pageId, idx) => ({ page_id: pageId, order: idx + 1 }));

            const newLeadId = orderedSavedPageIds[0];
            const newLead = newLeadId ? savedFunnelPages.find(p => p.page_id === newLeadId) : null;

            if (newLead && newLead.routing && newLead.referencePageId) {
                stanNotify({
                    type: 'info',
                    title: 'Entry page cannot have branches',
                    text: 'Detached pages from the new entry page.',
                    duration: 4000,
                });
                updateFunnelPage.mutate(
                    {
                        page_id: newLead.page_id,
                        referencePageId: newLead.referencePageId,
                        productTitle: newLead.title,
                        order: 1,
                        publish: newLead.status === PageStatus.PUBLISHED,
                        topBannerText: newLead.topBannerText,
                        funnelPrice: newLead.funnelPrice,
                        funnelCtaText: newLead.funnelCtaText,
                        routing: null,
                    },
                    {
                        onSuccess: () => reorderFunnelPages.mutate(payload),
                    }
                );
                return;
            }

            reorderFunnelPages.mutate(payload);
        },
        [storeId, savedFunnelPages, reorderFunnelPages, updateFunnelPage]
    );
}
